import axios from 'axios';
import { BigNumber } from 'bignumber.js';

import { datadogRum } from 'root/datadog/datadogRum';

import CustomerService from '../CustomerService';
import { CustomerData } from './CreditAccountService';
import DepositUsageIntentService from './DepositUsageIntentService';
import InvoicePaymentInstructionService from './InvoicePaymentInstructionService';

export interface DepositAllocation {
  creditMemoId: string;
  amount: string;
}

export interface InvoiceAllocation {
  invoiceId: string;
  amount: string;
}

export interface CreateAndCompleteSaleWithDepositsParams {
  accountId: string;
  customerId: string;
  creditAccountId: string;
  currency: string;
  shopId: string;
  registerId: string;
  depositAllocations: DepositAllocation[];
  invoiceAllocations: InvoiceAllocation[];
}

export interface DepositPaymentSaleResult {
  success: boolean;
  saleId?: string;
  error?: Error;
}

interface SaleApiResponse {
  Sale?: {
    saleID: string;
  };
}

export default class DepositPaymentSaleService {
  static async createAndCompleteSaleWithDeposits(
    params: CreateAndCompleteSaleWithDepositsParams
  ): Promise<DepositPaymentSaleResult> {
    const {
      accountId,
      customerId,
      creditAccountId,
      currency,
      shopId,
      registerId,
      depositAllocations,
      invoiceAllocations,
    } = params;

    const employeeID = window.merchantos.session.getEmployee()?.employeeID;

    let saleId: string | undefined;

    try {
      const totalAmountBN = depositAllocations.reduce((sum, allocation) => {
        const parsed = new BigNumber(allocation.amount);
        if (parsed.isNaN()) {
          throw new Error(`Invalid deposit allocation amount: "${allocation.amount}"`);
        }
        return sum.plus(parsed);
      }, new BigNumber(0));
      const totalAmount = parseFloat(totalAmountBN.toFixed(2));

      saleId = await this.createIncompleteSale({
        accountId,
        customerId,
        shopId,
        registerId,
        employeeID,
      });

      const customer = await this.fetchCustomerForRetry(customerId);

      await Promise.all([
        DepositUsageIntentService.upsertDepositUsageIntent({
          creditAccountId,
          customerId,
          saleId,
          totalAmount,
          currency,
          creditMemoAllocations: depositAllocations.map((d) => ({
            creditMemoId: d.creditMemoId,
            amount: d.amount,
          })),
          customer,
        }),
        InvoicePaymentInstructionService.upsertInvoicePaymentInstruction({
          creditAccountId,
          saleId,
          invoiceAllocations: invoiceAllocations.map((i) => ({
            invoiceId: i.invoiceId,
            amount: i.amount,
          })),
          currency,
          customer,
        }),
      ]);

      await this.completeSale({ accountId, saleId });

      return { success: true, saleId };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      datadogRum.addError(err, {
        message: 'Failed to create and complete sale with deposit payment',
        accountId,
        customerId,
        creditAccountId,
        saleId,
      });

      if (saleId) {
        await this.cleanupFailedSale(accountId, creditAccountId, saleId);
      }

      return { success: false, saleId, error: err };
    }
  }

  private static async createIncompleteSale(params: {
    accountId: string;
    customerId: string;
    shopId: string;
    registerId: string;
    employeeID?: string;
  }): Promise<string> {
    const { accountId, customerId, shopId, registerId, employeeID } = params;
    const url = `/API/Account/${accountId}/Sale.json`;
    const payload = {
      customerID: customerId,
      shopID: shopId,
      registerID: registerId,
      completed: false,
      ...(employeeID ? { employeeID } : {}),
    };

    const { data } = await axios.post<SaleApiResponse>(url, payload);
    const newSaleId = data?.Sale?.saleID;

    if (!newSaleId) {
      throw new Error('Sale creation succeeded but response did not include a saleID');
    }

    return newSaleId;
  }

  private static async completeSale(params: { accountId: string; saleId: string }): Promise<void> {
    await axios.put(`/API/Account/${params.accountId}/Sale/${params.saleId}.json`, {
      completed: true,
    });
  }

  private static async fetchCustomerForRetry(
    customerId: string
  ): Promise<CustomerData | undefined> {
    const customer = await CustomerService.getCustomer(customerId);
    return (customer as CustomerData | null) ?? undefined;
  }

  private static async cleanupFailedSale(
    accountId: string,
    creditAccountId: string,
    saleId: string
  ): Promise<void> {
    const operations = [
      'delete deposit usage intent',
      'delete invoice payment instruction',
      'void sale',
    ];
    const results = await Promise.allSettled([
      DepositUsageIntentService.deleteDepositUsageIntent(creditAccountId, saleId),
      InvoicePaymentInstructionService.deleteInvoicePaymentInstruction(creditAccountId, saleId),
      axios.put(`/API/Account/${accountId}/Sale/${saleId}.json`, { voided: true }),
    ]);

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        datadogRum.addError(
          result.reason instanceof Error ? result.reason : new Error(String(result.reason)),
          {
            message: `Cleanup failed: ${operations[index]}`,
            accountId,
            creditAccountId,
            saleId,
          }
        );
      }
    });
  }
}
